/**
 * SocialPurchaseDetailPage — iter264
 *
 * Klickbare Kaufübersicht für einen digitalen Social-Paid-Media-Kauf.
 * Zeigt: Content-Vorschau, Titel/Creator/Preis/Datum, Zahlungs-/Lieferstatus,
 * Download-Button (persönliche Kopie) und die zugehörigen Belege (Dokumente).
 * Analog zur physischen OrderDetailPage — für ALLE Sparten klickbar.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  ArrowLeft, Loader2, FileText, Download, ShoppingBag, CheckCircle,
  Clock, XCircle, AlertOctagon, FileImage,
} from "lucide-react";
import PaidMediaDownloadButton from "@/components/paid-media/PaidMediaDownloadButton";

function fmtEuro(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} €`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

const DOC_LABEL = {
  ORDER_CONFIRMATION: "Bestellübersicht",
  SELLER_PAYOUT_STATEMENT: "Auszahlungsabrechnung",
  REFUND_RECEIPT: "Erstattungsbeleg",
  PAYOUT_ADJUSTMENT: "Abrechnungskorrektur",
};

export default function SocialPurchaseDetailPage() {
  const { t } = useTranslation();
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data: d } = await api.get(`/me/purchases/social/${purchaseId}`);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kauf nicht gefunden");
    } finally { setLoading(false); }
  }, [purchaseId]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  const state = data?.purchase_state;
  const statusBadge = (() => {
    if (state === "REVOKED_LEGAL_TAKEDOWN") return { label: "Zugriff entzogen", cls: "border-red-300 text-red-700 bg-red-50", Icon: XCircle };
    if (state === "REFUNDED") return { label: "Erstattet", cls: "border-amber-300 text-amber-700 bg-amber-50", Icon: AlertOctagon };
    if (state === "DELIVERED") return { label: "Ausgeliefert", cls: "border-emerald-400 text-emerald-800 bg-emerald-50", Icon: CheckCircle };
    if (state === "DOWNLOAD_AVAILABLE") return { label: "Bereit zum Download", cls: "border-indigo-400 text-indigo-700 bg-indigo-50", Icon: Download };
    if (data?.delivery_render_status === "RENDERING" || data?.delivery_render_status === "PENDING")
      return { label: "Wird vorbereitet", cls: "border-slate-300 text-slate-700 bg-slate-50", Icon: Clock };
    return { label: "Gekauft", cls: "border-green-300 text-green-700 bg-green-50", Icon: ShoppingBag };
  })();

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || `beleg_${docId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 5000);
    } catch { window.alert(t("abo.receipt_load_error")); }
  };

  return (
    <div data-testid="social-purchase-detail" className="w-full max-w-3xl mx-auto py-6 px-4">
      <button
        data-testid="social-purchase-back"
        onClick={() => navigate("/profile/purchases?tab=social")}
        className="flex items-center gap-2 text-sm text-[#666] hover:text-[#0A0A0A] mb-4"
      >
        <ArrowLeft size={16} /> {t("common.back", { defaultValue: "Zurück" })}
      </button>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : error ? (
        <p data-testid="social-purchase-error" className="text-center text-sm text-red-600 py-16">{error}</p>
      ) : data ? (
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-2xl bg-[#F5F5F5] overflow-hidden shrink-0 flex items-center justify-center">
              {data.asset?.thumbnail_url ? (
                <img src={data.asset.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : <FileImage size={28} className="text-[#CCC]" />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 data-testid="social-purchase-title" className="text-xl font-bold text-[#0A0A0A]">{data.asset?.title || data.asset_id}</h1>
              <p className="text-sm text-[#666] mt-0.5">{data.creator?.display_name || data.creator?.handle}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span data-testid="social-purchase-status" className={`text-[11px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${statusBadge.cls}`}>
                  <statusBadge.Icon size={11} /> {statusBadge.label}
                </span>
                <span className="text-[11px] text-[#999]">{fmtDate(data.purchased_at)}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-lg font-bold text-[#0A0A0A]">{fmtEuro(data.price ?? data.asset?.price_eur)}</p>
            </div>
          </div>

          {/* Kaufübersicht */}
          <div className="border border-[#E5E5E5] rounded-2xl p-4">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-3">{t("purchase_detail.overview", { defaultValue: "Kaufübersicht" })}</h2>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-[#666]">Bestellnummer</dt>
              <dd data-testid="social-purchase-order-no" className="text-right text-[#0A0A0A]">{data.order?.order_id ? String(data.order.order_id).toUpperCase() : "—"}</dd>
              <dt className="text-[#666]">Zahlungsart</dt>
              <dd className="text-right text-[#0A0A0A]">{data.order?.payment_method || "—"}</dd>
              <dt className="text-[#666]">Zahlungsstatus</dt>
              <dd className="text-right text-[#0A0A0A]">{data.order?.payment_status === "paid" ? "Bezahlt" : (data.order?.payment_status || "—")}</dd>
              <dt className="text-[#666]">Betrag</dt>
              <dd className="text-right font-semibold text-[#0A0A0A]">{fmtEuro(data.order?.price ?? data.price)}</dd>
            </dl>
          </div>

          {/* Persönliche Kopie / Download */}
          <div className="border border-[#E5E5E5] rounded-2xl p-4">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-3">{t("purchase_detail.your_copy", { defaultValue: "Deine persönliche Kopie" })}</h2>
            <PaidMediaDownloadButton purchase={data} onUpdate={() => load()} />
          </div>

          {/* Belege */}
          <div className="border border-[#E5E5E5] rounded-2xl p-4">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-3">{t("purchase_detail.documents", { defaultValue: "Belege" })}</h2>
            {(!data.documents || data.documents.length === 0) ? (
              <p data-testid="social-purchase-no-docs" className="text-sm text-[#999]">{t("purchase_detail.no_documents", { defaultValue: "Noch keine Belege verfügbar." })}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.documents.map((d) => (
                  <li key={d.document_id}>
                    <button
                      data-testid={`social-purchase-doc-${d.document_id}`}
                      onClick={() => downloadDoc(d.document_id, d.filename)}
                      className="w-full flex items-center gap-3 border border-[#E5E5E5] rounded-xl p-3 hover:bg-[#FAFAFA] text-left"
                    >
                      <FileText size={18} className="text-[#0A0A0A] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A0A0A] truncate">{DOC_LABEL[d.doc_type] || d.doc_type || "Beleg"}</p>
                        <p className="text-xs text-[#666]">{d.document_number}</p>
                      </div>
                      <Download size={16} className="text-[#666] shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
